import { Component, Inject, OnDestroy, ViewChild } from '@angular/core'
import { transactionTypesVal } from '@helios/common-api'
import {
  BulkCashEntryTransaction,
  BulkTransaction,
  BulkTransactionModalMode,
  BulkTransactionsModalService,
  CashEntryInputModel,
  CreateEditTransactionService,
  CreateTransactionsModalService,
  TranCategoryUrl,
  TransactionModalMode,
} from '@helios/create-transactions-modal'
import {
  ResizeHandleState,
  SecuritiesSelectedComponent,
} from '@helios/generic-components'
import {
  EventType,
  HeliosMessageType,
  RowUpdateEvent,
  SessionMessagingService,
} from '@helios/messaging'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import {
  RootState,
  selectActiveSavedViews,
  selectActiveSession,
  selectActiveWindowDate,
} from '@helios/state-management'
import { NgbNav } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { Daterange, ToastService } from '@northfork/ng-basics'
import { ComponentContainer } from 'golden-layout'
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import {
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators'
import {
  DistributionsAccruedInterestGridKey,
  DistributionsFactoryHistoryGridKey,
  DistributionsKey,
  DistributionsMainGridKey,
} from '../panel-key'
import { AccruedInterestGridComponent } from './components/accrued-interest-grid/accrued-interest-grid.component'
import { AccruedInterestPropertiesComponent } from './components/accrued-interest-properties/accrued-interest-properties.component'
import { DistributionsGridComponent } from './components/distributions-grid/distributions-grid.component'
import { DistributionsPropertiesComponent } from './components/distributions-properties/distributions-properties.component'
import { FactorHistoryGridComponent } from './components/factor-history-grid/factor-history-grid.component'
import { MmfSettingsGridComponent } from './components/mmf-settings-grid/mmf-settings-grid.component'
import {
  AccruedInterest,
  InterestFormat,
} from './models/accrued-interest.model'
import { CouponRateHistory } from './models/coupon-rate-history.model'
import {
  Distribution,
  QuickEnterPredictionAction,
} from './models/distribution.model'
import { DividendHistory } from './models/dividend-history.model'
import { FactorHistory } from './models/factor-history.model'
import { MMFSettings } from './models/mmf-settings.model'
import { DistributionsService } from './services/distributions.service'
import { getDefaultDistributionsDaterange } from './utils/daterange-utils'
import {
  createTranCommandForBulkPredictionEntry,
  createTranCommandForSinglePredictionEntry,
} from './utils/quick-enter-prediction-utils'
/* eslint-disable */
import { BulkTransactionRequest } from 'libs/create-transactions-modal/src/lib/model/bulk-transaction/bulk-transaction.model'
import { CreateEditTransactionRequest } from 'libs/create-transactions-modal/src/lib/model/single-transaction/create-edit-transaction.model'
import { SecurityData } from './models/security-data.model'
/* eslint-enable */

interface DistributionsPanelState {
  resizeHandleState: ResizeHandleState
}

@Component({
  selector: 'hs-distributions',
  templateUrl: './distributions-panel.component.html',
  styleUrls: ['./distributions-panel.component.scss'],
})
export class DistributionsPanelComponent
  extends BasePanelComponent<DistributionsPanelState>
  implements OnDestroy
{
  panelKey = DistributionsKey
  activeTab = 1
  @ViewChild('securitiesSelectedComponent')
  readonly securitiesSelectedComponent!: SecuritiesSelectedComponent
  @ViewChild('distributionsGrid')
  readonly distributionsGrid!: DistributionsGridComponent
  @ViewChild('distributionsProperties')
  readonly distributionsProperties!: DistributionsPropertiesComponent
  @ViewChild('subPanelsNav')
  readonly subPanelsNav!: NgbNav
  @ViewChild('accruedInterestGrid')
  readonly accruedInterestGrid?: AccruedInterestGridComponent
  @ViewChild('accruedInterestProperties')
  readonly accruedInterestProperties?: AccruedInterestPropertiesComponent
  @ViewChild('factorHistoryGrid')
  readonly factorHistoryGrid?: FactorHistoryGridComponent
  @ViewChild('mmfSettingsGrid')
  readonly mmfSettingsGrid?: MmfSettingsGridComponent

  readonly windowDate$: Observable<Date | null> = this.store
    .select(selectActiveWindowDate)
    .pipe(
      distinctUntilChanged(),
      map(windowDate => {
        if (windowDate == undefined) {
          return null
        }
        return windowDate
      }),
    )

  readonly accountId$ = this.store.select(selectActiveSession).pipe(
    take(1),
    map(session => {
      if (session) return session.loadedAccountIds[0]
      else return undefined
    }),
  )

  // Find the saved view state with the specified grid key
  readonly savedViewsState$ = this.store
    .select(selectActiveSavedViews)
    .pipe(
      map(savedViews =>
        savedViews?.filter(view =>
          [
            DistributionsMainGridKey,
            DistributionsAccruedInterestGridKey,
            DistributionsFactoryHistoryGridKey,
          ].includes(view.gridKey),
        ),
      ),
    )

  readonly distributionsDateRange$ = new BehaviorSubject<Daterange | null>(null)

  readonly accruedInterestDateRange$ = new BehaviorSubject<Daterange | null>(
    null,
  )

  readonly interestFormat$ = new BehaviorSubject<InterestFormat>(
    InterestFormat.Decimal,
  )

  readonly selectedSecuritySubject$ = new BehaviorSubject<number | null>(null)

  showMMFTab = false

  private readonly updatedTransactions$: Observable<void> =
    this.messagingService
      .on<unknown, RowUpdateEvent>(
        EventType.MESSAGE,
        HeliosMessageType.CWTransactionUpdate,
      )
      .pipe(
        startWith(null),
        map(() => undefined),
      )

  distributions$: Observable<Distribution[] | null> = this.triggerDistribution()

  hasLlaRates = false

  readonly accruedInterest$: Observable<AccruedInterest[] | null> =
    combineLatest([
      this.selectedSecuritySubject$,
      this.accruedInterestDateRange$,
      this.interestFormat$,
      this.updatedTransactions$,
    ]).pipe(
      switchMap(([selectedSecurityId, dateRange, interestFormat]) => {
        if (selectedSecurityId == null) {
          return of([])
        }

        switch (interestFormat) {
          case InterestFormat.Decimal:
            return this.distributionsService
              .getAccruedInterestByRate(selectedSecurityId, dateRange)
              .pipe(startWith(null)) // trigger the loading spinner with null data
          case InterestFormat.Dollar:
            return this.distributionsService
              .getAccruedInterestByAmount(selectedSecurityId, dateRange)
              .pipe(startWith(null)) // trigger the loading spinner with null data
        }
      }),
    )

  readonly factorHistory$: Observable<FactorHistory[] | null> = combineLatest([
    this.selectedSecuritySubject$,
    this.updatedTransactions$,
  ]).pipe(
    switchMap(([selectedSecurityId]) => {
      if (selectedSecurityId == null) {
        return of([])
      }

      return this.distributionsService
        .getFactorHistory(selectedSecurityId)
        .pipe(startWith(null)) // trigger the loading spinner with null data
    }),
  )

  readonly couponRateHistory$: Observable<CouponRateHistory[] | null> =
    combineLatest([
      this.selectedSecuritySubject$,
      this.updatedTransactions$,
    ]).pipe(
      switchMap(([selectedSecurityId]) => {
        if (selectedSecurityId == null) {
          return of([])
        }

        return this.distributionsService
          .getCouponRateHistory(selectedSecurityId)
          .pipe(startWith(null)) // trigger the loading spinner with null data
      }),
    )

  readonly dividendHistory$: Observable<DividendHistory[] | null> =
    combineLatest([
      this.selectedSecuritySubject$,
      this.updatedTransactions$,
    ]).pipe(
      switchMap(([selectedSecurityId]) => {
        if (selectedSecurityId == null) {
          return of([])
        }

        return this.distributionsService
          .getDividendHistory(selectedSecurityId)
          .pipe(startWith(null)) // trigger the loading spinner with null data
      }),
    )

  readonly mmfSettings$: Observable<MMFSettings | null> = combineLatest([
    this.selectedSecuritySubject$,
    this.accountId$,
    this.updatedTransactions$,
  ]).pipe(
    switchMap(([selectedSecurityId, accountId]) => {
      if (selectedSecurityId == null || accountId == undefined) {
        return of(null)
      }

      return this.distributionsService
        .getMMFSettings(selectedSecurityId, accountId)
        .pipe(startWith(null)) // trigger the loading spinner with null data
    }),
  )

  readonly securityId$ = combineLatest([this.selectedSecuritySubject$]).pipe(
    switchMap(selectedSecurityId => {
      if (selectedSecurityId == null) {
        return of(null)
      }

      return selectedSecurityId
    }),
  )

  activeAssetId?: string
  readonly securityData$ = this.selectedSecuritySubject$.pipe(
    switchMap(securityId => {
      if (securityId == null) {
        return of(null)
      }
      const data = this.distributionsService.getSecurityData(securityId)
      data.pipe(take(1)).subscribe(result => {
        this.activeAssetId = result.assetId
      })
      return data
    }),
  )

  constructor(
    public createEditTransactionService: CreateEditTransactionService,
    public toastService: ToastService,
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly createTransactionsModalService: CreateTransactionsModalService,
    private readonly bulkTransactionModalService: BulkTransactionsModalService,
    private readonly distributionsService: DistributionsService,
    private readonly store: Store<RootState>,
    private readonly messagingService: SessionMessagingService,
  ) {
    super(container)
    this.windowDate$.subscribe(windowDate => {
      this.distributionsDateRange$.next(
        getDefaultDistributionsDaterange(windowDate),
      )
      this.accruedInterestDateRange$.next(
        getDefaultDistributionsDaterange(windowDate),
      )
    })
  }

  ngOnDestroy() {
    this.interestFormat$.complete()
    this.distributionsDateRange$.complete()
    this.accruedInterestDateRange$.complete()
  }

  triggerDistribution() {
    return combineLatest([
      this.selectedSecuritySubject$,
      this.distributionsDateRange$,
      this.updatedTransactions$,
    ]).pipe(
      switchMap(([selectedSecurityId, dateRange]) => {
        if (selectedSecurityId == null) {
          return of([])
        }

        return this.distributionsService
          .getDistribution(selectedSecurityId, dateRange)
          .pipe(startWith(null)) // trigger the loading spinner with null data
      }),
      tap(
        rows =>
          (this.hasLlaRates =
            Array.isArray(rows) && rows.some(d => d.isLlaRate)),
      ),
    )
  }

  /** @internal */
  onEnterPrediction(dist: Distribution[]) {
    if (dist.length === 1) {
      const prePopulatedInput: CashEntryInputModel = {
        tranTypeCode: dist[0].tranTypeCode,
        entryDate: dist[0].calculatedEntryDate,
        settleDate: dist[0].calculatedSettleDate,
        postDate: dist[0].calculatedPostDate,
        amount: String(dist[0].calculatedAmount),
        secId: dist[0].secId,
        accountId: dist[0].acctId,
        securityId: this.activeAssetId,
        currency: dist[0].currencyCode,
        collateralBroker: dist[0].collateralBroker,
        prepaidInterest: dist[0].calculatedPrepaidInterest,
        note: 'As per distributions panel',
        fromDistribution: true,
      }
      this.createTransactionsModalService.toggleCashEntryModal(
        {
          modalMode: TransactionModalMode.CreateCWTran, //it's not really panel name, it's more like what type of transaction you want to work with
        },
        prePopulatedInput,
      )
    } else if (dist.length > 1) {
      const bulkTransaction = this.mapDistributionToBulkTransaction(
        dist,
        this.activeAssetId,
      )
      this.bulkTransactionModalService.toggleCashEntryModal({
        modalMode: BulkTransactionModalMode.CreateCWTran,
        enterPrediction: bulkTransaction,
        isFromDistriBution: true,
        note: 'As per distributions panel',
      })
    }
  }

  onQuickEnterPrediction(data: QuickEnterPredictionAction) {
    const showInprogressMsg = () =>
      this.toastService.warning(
        'Clearwater Transaction create command in progress.',
        {
          closeButton: true,
          timeOut: 2000,
        },
        'In Progress',
      )

    let tranCommand: CreateEditTransactionRequest | BulkTransactionRequest
    if (data.distributionClicked.length === 1) {
      /* Single Prediction entry */
      tranCommand = createTranCommandForSinglePredictionEntry(data)
      if (data.sessionId) {
        showInprogressMsg()
        this.createEditTransactionService
          .createTransaction(data.sessionId, tranCommand)
          .subscribe(() =>
            this.toastService.success(
              'Clearwater Transaction create command successfully submitted.',
            ),
          )
      }
    } else if (data.distributionClicked.length > 1) {
      /* Bulk Prediction entry */
      tranCommand = createTranCommandForBulkPredictionEntry(data)
      this.createEditTransactionService
        .createBulkTransaction(tranCommand, TranCategoryUrl.CASH_ENTRY)
        .subscribe(() =>
          this.toastService.success(
            'Clearwater Transaction create command successfully submitted.',
          ),
        )
    }
  }

  /** @internal */
  onEditTransaction(dist: Distribution[]) {
    const tranId = dist.map(distribution => {
      return distribution.tranId ?? ''
    })
    const secId = dist.map(distribution => {
      return distribution.secId
    })
    if (dist.length === 1) {
      this.createTransactionsModalService.toggleCreateTransactionModal(
        dist[0].tranTypeCode,
        {
          modalMode: TransactionModalMode.EditCWTran,
          transactionId: dist[0].tranId,
          origTransactionId: dist[0].legacyOrigId,
          securityId: dist[0].secId,
          transType: dist[0].tranTypeCode,
        },
      )
    } else {
      this.bulkTransactionModalService.toggleCashEntryModal({
        modalMode: BulkTransactionModalMode.EditCWTran,
        cwTranId: tranId,
        securityId: secId,
        isFromDistriBution: true,
      })
    }
  }

  /** @internal */
  onSecuritySelectionChanged(securities: number[]) {
    this.selectedSecuritySubject$.next(securities[0])
  }

  /** @internal */
  onDistributionsDateRangeChanged(dateRange: Daterange) {
    this.distributionsDateRange$.next(dateRange)
  }

  /** @internal */
  onAccruedInterestDateRangeChanged(dateRange: Daterange) {
    this.accruedInterestDateRange$.next(dateRange)
  }

  /** @internal */
  onAccruedInterestFormatChanged(interestFormat: InterestFormat) {
    this.interestFormat$.next(interestFormat)
  }

  /** @internal */
  setSecurityDataForMMF(securityData: SecurityData) {
    this.showMMFTab =
      securityData &&
      (securityData.securityType == 'MMFUND' ||
        securityData?.securitySubType == 'Open-End Fund')
        ? true
        : false
  }
  /** @internal */
  onRefreshDistribution(refresh: string) {
    this.distributions$ = this.triggerDistribution()
  }

  onResizeFinished(resizeHandleState: ResizeHandleState) {
    this.setState({
      ...this.state,
      resizeHandleState,
    })
  }

  mapDistributionToBulkTransaction(
    distribution: Distribution[],
    activeAssetId?: string,
  ): BulkTransaction[] {
    const transaction: BulkTransaction[] = []
    distribution.forEach(dist => {
      const bulkTran: BulkCashEntryTransaction = {
        tranType:
          transactionTypesVal.find(val => val.code === dist.tranTypeCode)
            ?.type ?? 'UNSUPPORTED',
        entryDate: dist.calculatedEntryDate ?? '',
        settleDate: dist.calculatedSettleDate ?? '',
        postDate: dist.calculatedPostDate ?? '',
        amount: dist.calculatedAmount ?? 0,
        accountId: dist.acctId,
        cusip: activeAssetId,
        currencyCode: dist.currencyCode,
        dataMap: {
          collateralBroker: dist.collateralBroker,
          prepaidInterest: dist.calculatedPrepaidInterest ?? 0.0,
        },
        userId: 0,
        securityId: dist.secId,
      }
      transaction.push(bulkTran)
    })
    return transaction
  }
}
